import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { format } from "date-fns";

interface Props {
  tripId: string;
  userId: string;
}

interface Expense {
  id: string;
  title: string;
  category: string;
  amount: number; // Anteil des Teilnehmers
  created_at: string | null;
}

interface Payment {
  id: string;
  amount: number;
  created_at: string | null;
}

function safeFormatDate(dateString: string | null): string {
  if (!dateString) return "Unbekannt";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return "Unbekannt";
  }
  return format(date, "dd.MM.yyyy");
}

export default function ParticipantFinanceOverview({ tripId, userId }: Props) {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tripId || !userId) return;

    const fetchData = async () => {
      setLoading(true);

      // 1. Alle Shares für diesen User abrufen
      const { data: sharesData, error: sharesError } = await supabase
        .from("trip_expense_shares")
        .select("expense_id, amount")
        .eq("user_id", userId);

      if (sharesError) {
        console.error(
          "Fehler beim Laden der Ausgabenanteile:",
          sharesError.message
        );
        setLoading(false);
        return;
      }

      const expenseIds = sharesData?.map((s) => s.expense_id) ?? [];

      // 2. Alle zugehörigen Ausgaben abrufen
      const { data: expenseData, error: expenseError } = await supabase
        .from("trip_expenses")
        .select("id, title, category, amount, created_at")
        .in("id", expenseIds)
        .eq("trip_id", tripId);

      if (expenseError) {
        console.error("Fehler beim Laden der Ausgaben:", expenseError.message);
        setLoading(false);
        return;
      }

      const parsedExpenses: Expense[] = sharesData.map((share) => {
        const matched = expenseData?.find((e) => e.id === share.expense_id);
        return {
          id: share.expense_id,
          title: matched?.title ?? "Unbekannt",
          category: matched?.category ?? "Unbekannt",
          created_at: matched?.created_at ?? "",
          amount: share.amount, // Anteil des Nutzers
        };
      });

      // 3. Alle Zahlungen des Nutzers
      const { data: paymentData, error: paymentError } = await supabase
        .from("trip_payments")
        .select("id, amount, created_at")
        .eq("trip_id", tripId)
        .eq("user_id", userId);

      if (paymentError) {
        console.error("Fehler beim Laden der Zahlungen:", paymentError.message);
      }

      setExpenses(parsedExpenses);
      setPayments(paymentData ?? []);
      setLoading(false);
    };

    fetchData();
  }, [tripId, userId]);

  const totalOwed = expenses.reduce((sum, e) => sum + e.amount, 0);
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const balance = totalPaid - totalOwed;

  if (loading) {
    return <p className="text-sm text-gray-500">Lade Finanzen...</p>;
  }

  return (
    <div className="mt-6">
      <h2 className="text-lg font-semibold mb-2">💰 Reisekasse</h2>
      <p className="text-sm text-gray-700 mb-4">
        Dein Anteil: {totalOwed.toFixed(2)} – Deine Zahlungen:{" "}
        {totalPaid.toFixed(2)} →{" "}
        <strong className={balance < 0 ? "text-red-600" : "text-green-600"}>
          {balance.toFixed(2)} {balance < 0 ? "nachzahlen" : "Guthaben"}
        </strong>
      </p>

      <div className="mb-4">
        <h3 className="font-medium text-sm text-gray-600 mb-1">
          🧾 Deine Ausgabenanteile
        </h3>
        {expenses.length === 0 ? (
          <p className="text-sm italic text-gray-500">Keine Ausgabenanteile</p>
        ) : (
          <ul className="text-sm text-gray-800 space-y-1">
            {expenses
              .sort((a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? ""))
              .map((e) => (
                <li key={e.id} className="flex justify-between">
                  <span>
                    {safeFormatDate(e.created_at)} – {e.title} ({e.category})
                  </span>
                  <span>{e.amount.toFixed(2)}</span>
                </li>
              ))}
          </ul>
        )}
      </div>

      <div>
        <h3 className="font-medium text-sm text-gray-600 mb-1">
          💳 Deine Zahlungen
        </h3>
        {payments.length === 0 ? (
          <p className="text-sm italic text-gray-500">
            Keine Zahlungen erfasst
          </p>
        ) : (
          <ul className="text-sm text-gray-800 space-y-1">
            {payments
              .sort((a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? ""))
              .map((p) => (
                <li key={p.id} className="flex justify-between">
                  <span>{safeFormatDate(p.created_at)}</span>
                  <span className="text-green-600">
                    - {p.amount.toFixed(2)}
                  </span>
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}
